import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  ShoppingTrip,
  ShoppingTripItem,
  CompleteTripRequest,
  CompleteTripResponse,
  ScanResponse,
  ConfirmMatchRequest,
  ConfirmMatchResponse,
} from '@pantry/shared';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch the active (in-progress) trip for a shopping list, if any.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 */
async function getActiveTrip(householdId: string, listId: string): Promise<ShoppingTrip | null> {
  return apiFetch<ShoppingTrip | null>(
    `/households/${householdId}/shopping-lists/${listId}/trips/active`
  );
}

/**
 * Start a new shopping trip for a list.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 */
async function startTrip(householdId: string, listId: string): Promise<ShoppingTrip> {
  return apiFetch<ShoppingTrip>(`/households/${householdId}/shopping-lists/${listId}/trips`, {
    method: 'POST',
  });
}

/**
 * Complete a shopping trip, optionally adding items to inventory.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param tripId - The trip ID
 * @param data - Which trip items to add to inventory
 */
async function completeTrip(
  householdId: string,
  listId: string,
  tripId: string,
  data: CompleteTripRequest
): Promise<CompleteTripResponse> {
  return apiFetch<CompleteTripResponse>(
    `/households/${householdId}/shopping-lists/${listId}/trips/${tripId}/complete`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Cancel an active shopping trip.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param tripId - The trip ID
 */
async function cancelTrip(
  householdId: string,
  listId: string,
  tripId: string
): Promise<ShoppingTrip> {
  return apiFetch<ShoppingTrip>(
    `/households/${householdId}/shopping-lists/${listId}/trips/${tripId}/cancel`,
    {
      method: 'POST',
    }
  );
}

/**
 * List all scanned items for a shopping trip.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param tripId - The trip ID
 */
async function listTripItems(
  householdId: string,
  listId: string,
  tripId: string
): Promise<ShoppingTripItem[]> {
  return apiFetch<ShoppingTripItem[]>(
    `/households/${householdId}/shopping-lists/${listId}/trips/${tripId}/items`
  );
}

/**
 * Scan a barcode during an active shopping trip.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param tripId - The trip ID
 * @param barcode - The scanned barcode string
 */
async function scanBarcode(
  householdId: string,
  listId: string,
  tripId: string,
  barcode: string
): Promise<ScanResponse> {
  return apiFetch<ScanResponse>(
    `/households/${householdId}/shopping-lists/${listId}/trips/${tripId}/scan`,
    {
      method: 'POST',
      body: JSON.stringify({ barcode }),
    }
  );
}

/**
 * Confirm a product-group match for an ambiguous scan result.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param tripId - The trip ID
 * @param data - The match confirmation payload
 */
async function confirmScanMatch(
  householdId: string,
  listId: string,
  tripId: string,
  data: ConfirmMatchRequest
): Promise<ConfirmMatchResponse> {
  return apiFetch<ConfirmMatchResponse>(
    `/households/${householdId}/shopping-lists/${listId}/trips/${tripId}/confirm-match`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch the active shopping trip for a list.
 * Returns null when no trip is currently in progress.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 */
export function useActiveTrip(householdId: string, listId: string) {
  return useQuery({
    queryKey: queryKeys.shoppingTrips.active(householdId, listId),
    queryFn: () => getActiveTrip(householdId, listId),
    enabled: !!householdId && !!listId,
    staleTime: 30 * 1000, // 30 seconds — trips change frequently during shopping
  });
}

/**
 * Hook to fetch all scanned items for a shopping trip.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param tripId - The trip ID
 */
export function useTripItems(
  householdId: string,
  listId: string,
  tripId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.shoppingTrips.items(householdId, listId, tripId),
    queryFn: () => listTripItems(householdId, listId, tripId),
    enabled: (options?.enabled ?? true) && !!householdId && !!listId && !!tripId,
    staleTime: 15 * 1000, // 15 seconds — scan results update frequently
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to start a new shopping trip.
 * Invalidates the active trip query and the shopping lists cache on success.
 */
export function useStartTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, listId }: { householdId: string; listId: string }) =>
      startTrip(householdId, listId),
    onSuccess: (newTrip, { householdId, listId }) => {
      queryClient.setQueryData(queryKeys.shoppingTrips.active(householdId, listId), newTrip);
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingTrips.active(householdId, listId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
    },
  });
}

/**
 * Hook to cancel an active shopping trip.
 * Invalidates the active trip and shopping lists caches on success.
 * List items that were checked during the trip will be unchecked by the server.
 */
export function useCancelTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      tripId,
    }: {
      householdId: string;
      listId: string;
      tripId: string;
    }) => cancelTrip(householdId, listId, tripId),
    onSuccess: (_, { householdId, listId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingTrips.active(householdId, listId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
    },
  });
}

/**
 * Hook to complete a shopping trip.
 * Invalidates the active trip, shopping lists, and items caches on success
 * since completing a trip may create new inventory items.
 */
export function useCompleteTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      tripId,
      data,
    }: {
      householdId: string;
      listId: string;
      tripId: string;
      data: CompleteTripRequest;
    }) => completeTrip(householdId, listId, tripId, data),
    onSuccess: (_, { householdId, listId, tripId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingTrips.active(householdId, listId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
      // Invalidate items since completing a trip adds items to inventory
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingTrips.items(householdId, listId, tripId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
    },
  });
}

/**
 * Hook to scan a barcode during an active shopping trip.
 * Invalidates the trip items and shopping list detail caches on success
 * since scanning may check off list items.
 */
export function useScanBarcode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      tripId,
      barcode,
    }: {
      householdId: string;
      listId: string;
      tripId: string;
      barcode: string;
    }) => scanBarcode(householdId, listId, tripId, barcode),
    onSuccess: (_, { householdId, listId, tripId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingTrips.items(householdId, listId, tripId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
    },
  });
}

/**
 * Hook to confirm a product-group match for an ambiguous scan.
 * Invalidates the trip items and shopping list detail caches on success.
 */
export function useConfirmScanMatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      tripId,
      data,
    }: {
      householdId: string;
      listId: string;
      tripId: string;
      data: ConfirmMatchRequest;
    }) => confirmScanMatch(householdId, listId, tripId, data),
    onSuccess: (_, { householdId, listId, tripId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingTrips.items(householdId, listId, tripId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
    },
  });
}
